import { cn } from '@/lib/utils';

/** Discrete, ordered phases of the withdraw flow. */
export type WithdrawStep = 'choose-method' | 'verification' | 'complete';

const STEPS: { step: WithdrawStep; label: string }[] = [
  { step: 'choose-method', label: 'Choose Method' },
  { step: 'verification', label: 'Verification' },
  { step: 'complete', label: 'Withdraw Complete' },
];

export interface WithdrawStepperProps {
  /** The currently active step. */
  current: WithdrawStep;
}

/**
 * Three-step progress bar shown above the withdraw flow
 * (Choose Method → Verification → Withdraw Complete). Completed steps show a
 * checkmark; the active step and completed steps are purple, pending steps grey.
 */
export function WithdrawStepper({ current }: WithdrawStepperProps) {
  const currentIndex = STEPS.findIndex((s) => s.step === current);

  return (
    <div className="w-full">
      <div className="flex items-center px-5">
        {STEPS.map(({ step }, i) => {
          // The final step is only ever current on the success page, so being
          // active there means the flow is done — render it checked.
          const isCompleted = i < currentIndex || (i === currentIndex && step === 'complete');
          const isActive = i === currentIndex && !isCompleted;
          const isLast = i === STEPS.length - 1;

          return (
            <div key={step} className={cn('flex items-center', !isLast && 'flex-1')}>
              <div
                data-testid={`withdraw-stepper-${step}`}
                className={cn(
                  'flex items-center justify-center size-6 rounded-full border-2 transition-colors shrink-0',
                  isCompleted && 'bg-purple border-purple text-white',
                  isActive && 'bg-surface border-purple',
                  !isCompleted && !isActive && 'bg-surface border-border',
                )}
              >
                {isCompleted && (
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path
                      d="M2.5 7.5L5.5 10.5L11.5 3.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              {!isLast && <div className={cn('flex-1 h-[2px] mx-1', i < currentIndex ? 'bg-purple' : 'bg-border')} />}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-3 mt-1">
        {STEPS.map(({ step, label }, i) => {
          const isCompleted = i < currentIndex;
          const isActive = i === currentIndex;
          return (
            <span
              key={step}
              className={cn(
                'text-[12.6px] capitalize',
                i === 0 && 'justify-self-start text-left',
                i === STEPS.length - 1 && 'justify-self-end text-right',
                i !== 0 && i !== STEPS.length - 1 && 'justify-self-center text-center',
                isActive || isCompleted ? 'text-purple font-medium' : 'text-muted',
              )}
            >
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
